// =============================
// 1) بنية Firestore المقترحة
// =============================
// Collection: catalog_pages
//   - docId (auto)
//   - slug: string (مثال: "cakes", "cookies", "sale") [Unique]
//   - title: string (يُعرض بالواجهة)
//   - isActive: boolean
//   - filters: {
//       categories: ["big-cakes","mini-cakes"],   // optional
//       tags: ["new","promo"],                    // optional
//       inStockOnly: true                            // optional
//     }
//   - sort: { by: "createdAt" | "price" | "name" | "orderedTimes", dir: "asc" | "desc" }
//
// Collection: Products
//   - name: string
//   - price: number
//   - imageUrl: string
//   - category: string
//   - tags: [string]
//   - inStock: boolean
//   - createdAt: Timestamp
//   - orderedTimes: number
//   - variations?: [ { id: string, name: string, priceDelta?: number } ]
//
// Collection: Orders
//   - orderNumber: number
//   - Data: {
//       selectedProducts: [
//         {
//           productId: string,
//           name: string,
//           unitPrice: number,
//           quantity: number,
//           imageUrl?: string,
//           variationId?: string,
//           variationName?: string
//         }
//       ]
//     }


// =====================================
// 2) router.js – إضافة مسار ديناميكي
// =====================================
// استيراد الصفحة:
// import CatalogPage from '@/pages/CatalogPage.vue'
// إضافة المسار:
// { path: '/catalog/:slug', name: 'CatalogDynamic', component: CatalogPage }


// ============================================================
// 3) src/pages/CatalogPage.vue – صفحة الكاتالوج الديناميكية
// ============================================================

/*
ملاحظات:
- الصفحة تقرأ إعداداتها من catalog_pages عبر slug.
- تعرض المنتجات مع بحث + فلترة + ترتيب + ترقيم صفحات (infinite scroll اختياري).
- أزرار: "أضف لطلبية جديدة" و"أضف لطلبية موجودة".
- للطلبية الجديدة نستعمل orderDraft (localStorage) بنفس أسلوبك selectedProducts.
- للطلبية الموجودة بنفتح مودال إدخال رقم الطلبية ونعمل ترانزاكشن لزيادة/إضافة المنتج.
*/

<template>
  <div class="catalogContainer">
    <div class="toolbar">
      <div class="title">
        <h2>{{ pageTitle }}</h2>
        <small v-if="pageReady" class="muted">/catalog/{{ slug }}</small>
      </div>
      <div class="actions">
        <input type="text" v-model.trim="search" placeholder="חיפוש מוצר..." @input="debouncedReload"/>
        <select v-model="sort.by" @change="reload()">
          <option value="createdAt">الأحدث</option>
          <option value="price">السعر</option>
          <option value="name">الاسم</option>
          <option value="orderedTimes">الأكثر طلبًا</option>
        </select>
        <select v-model="sort.dir" @change="reload()">
          <option value="desc">تنازلي</option>
          <option value="asc">تصاعدي</option>
        </select>
      </div>
    </div>

    <div class="hint muted" v-if="pageReady">
      <span>מציג {{ totalCountShown }} מוצרים</span>
      <span v-if="activeFiltersText"> • {{ activeFiltersText }}</span>
    </div>

    <div class="grid">
      <div class="card" v-for="p in products" :key="p.id">
        <div class="thumb" :style="{backgroundImage: `url(${p.imageUrl || fallbackImg})`}"/>
        <div class="info">
          <div class="name">{{ p.Name }}</div>
          <div class="price">₪ {{ priceWithVar(p).toFixed(2) }}</div>
          <div class="vars" v-if="(p.variations && p.variations.length)">
            <select v-model="selectedVar[p.id]">
              <option :value="null">بدون متغير</option>
              <option v-for="v in p.variations" :key="v.id" :value="v.id">{{ v.name }}</option>
            </select>
          </div>
        </div>
        <div class="cta">
          <button @click="addToNewOrder(p)">أضف لطلبية جديدة</button>
          <button class="secondary" @click="openExistingModal(p)">أضف لطلبية موجودة</button>
        </div>
      </div>
    </div>

    <div class="loadMore" v-if="hasMore && !loading">
      <button @click="loadMore()">تحميل المزيد</button>
    </div>

    <div class="loadingModal" v-if="loading"><div class="loader"></div></div>

    <!-- مودال الطلبية الموجودة -->
    <div class="modal" v-if="existingModal">
      <div class="modal-box">
        <div class="modal-head">
          <h3>إضافة المنتج لطلبية موجودة</h3>
          <button class="close" @click="existingModal=false">×</button>
        </div>
        <div class="modal-body">
          <label>أدخل رقم الطلبية</label>
          <input type="number" v-model.number="existingOrderNumber" placeholder="مثال: 5978"/>
          <label>الكمية</label>
          <input type="number" v-model.number="existingQty" min="1"/>
          <p class="muted small">سيتم البحث بحقل <b>Orders.orderNumber</b>.</p>
          <p v-if="existingError" class="error">{{ existingError }}</p>
        </div>
        <div class="modal-foot">
          <button @click="confirmAddToExisting()">تأكيد الإضافة</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'

// Helper: debounce بسيط
function debounce (fn, wait=350) {
  let t
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait) }
}

// إدارة مسودة الطلب الجديد (localStorage)
const DRAFT_KEY = 'newOrderDraft'
function readDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{"selectedProducts":[]}') } catch(e){ return { selectedProducts: [] } }
}
function writeDraft(d){ localStorage.setItem(DRAFT_KEY, JSON.stringify(d)) }

export default {
  name: 'CatalogPage',
  data(){
    return {
      slug: this.$route.params.slug,
      pageTitle: 'كتالوج',
      pageReady: false,
      search: '',
      sort: { by: 'createdAt', dir: 'desc' },
      filters: { categories: [], tags: [], inStockOnly: false },
      products: [],
      pageSize: 24,
      lastDoc: null,
      hasMore: true,
      loading: false,
      selectedVar: {},
      fallbackImg: 'https://via.placeholder.com/400x300?text=Product',
      debouncedReload: null,

      // existing order modal
      existingModal: false,
      existingOrderNumber: null,
      existingQty: 1,
      existingProductCtx: null,
      existingError: ''
    }
  },
  computed:{
    totalCountShown(){ return this.products.length },
activeFiltersText(){
  const parts = []
  if (this.filters && this.filters.categories && this.filters.categories.length)
    parts.push(`تصنيفات: ${this.filters.categories.join(', ')}`)
  if (this.filters && this.filters.tags && this.filters.tags.length)
    parts.push(`وسوم: ${this.filters.tags.join(', ')}`)
  if (this.filters && this.filters.inStockOnly)
    parts.push('متوفر بالمخزون فقط')
  return parts.join(' • ')
}
  },
  created(){
    this.debouncedReload = debounce(this.reload, 350)
    this.loadPageConfig().then(() => this.reload())
  },
  watch:{
    '$route.params.slug': {
      handler(){
        this.slug = this.$route.params.slug
        this.pageReady = false
        this.products = []
        this.lastDoc = null
        this.hasMore = true
        this.loadPageConfig().then(() => this.reload())
      }, immediate: false
    }
  },
  methods:{
    async loadPageConfig(){
      const db = firebase.firestore()
      const snap = await db.collection('catalog_pages').where('slug','==', this.slug).limit(1).get()
      if (snap.empty) {
        this.pageTitle = 'كتالوج'
        this.filters = { categories: [], tags: [], inStockOnly: false }
        this.sort = { by: 'createdAt', dir: 'desc' }
        this.pageReady = true
        return
      }
      const doc = snap.docs[0]
      const data = doc.data()
      this.pageTitle = data.title || 'كتالوج'
      this.filters = data.filters || { categories: [], tags: [], inStockOnly: false }
      this.sort = data.sort || { by: 'createdAt', dir: 'desc' }
      this.pageReady = true
    },

    buildQuery(baseLimit){
      const db = firebase.firestore()
      let q = db.collection('Products')

      // فلترة ديناميكية
if (this.filters && this.filters.categories && this.filters.categories.length) {
  q = q.where('category','in', this.filters.categories.slice(0,10))
}
if (this.filters && this.filters.inStockOnly) {
  q = q.where('inStock','==', true)
}

      // بحث بالاسم
      if (this.search) {
        // prefix search بسيط بالاسم (يتطلب فهرسة name_lower)
        const term = this.search.trim().toLowerCase()
        q = q.where('name_lower','>=', term).where('name_lower','<=', term + '\uf8ff')
      }

      // ترتيب
      const by = this.sort.by || 'createdAt'
      const dir = this.sort.dir === 'asc' ? 'asc' : 'desc'
      q = q.orderBy(by, dir)

      if (baseLimit) q = q.limit(baseLimit)
      if (this.lastDoc) q = q.startAfter(this.lastDoc)
      return q
    },

    async reload(){
      this.loading = true
      this.products = []
      this.lastDoc = null
      this.hasMore = true
      const snap = await this.buildQuery(this.pageSize).get()
      this.products = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.lastDoc = snap.docs[snap.docs.length - 1] || null
      this.hasMore = !!this.lastDoc && snap.docs.length === this.pageSize
      this.loading = false
    },

    async loadMore(){
      if (!this.hasMore || this.loading) return
      this.loading = true
      const snap = await this.buildQuery(this.pageSize).get()
      this.products.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })))
      this.lastDoc = snap.docs[snap.docs.length - 1] || null
      this.hasMore = !!this.lastDoc && snap.docs.length === this.pageSize
      this.loading = false
    },

    priceWithVar(p){
      const vid = this.selectedVar[p.id]
      if (!vid || !p.variations || !p.variations.length) return Number(p.price || 0)
    const v = p.variations.find(x => x.id === vid)
    const delta = (v && v.priceDelta) ? Number(v.priceDelta) : 0
      return Number(p.price || 0) + delta
    },
addToNewOrder(p){
  const draft = readDraft()
  const vid = this.selectedVar[p.id] || null
  const foundVar = vid && p.variations ? p.variations.find(x => x.id === vid) : null
  const vName = foundVar ? foundVar.name : ''
  const unitPrice = this.priceWithVar(p)

  // إذا المنتج موجود بنفس المتغير -> زد الكمية
  const idx = draft.selectedProducts.findIndex(it => it.productId === p.id && it.variationId === vid)
  if (idx > -1) {
    draft.selectedProducts[idx].quantity += 1
  } else {
    draft.selectedProducts.push({
      productId: p.id,
      name: p.name,
      unitPrice,
      quantity: 1,
      imageUrl: p.imageUrl || '',
      variationId: vid,
      variationName: vName
    })
  }
  writeDraft(draft)
  this.$toast && this.$toast.success('تمت الإضافة لمسودة طلبية جديدة')
},


    openExistingModal(p){
      this.existingProductCtx = p
      this.existingQty = 1
      this.existingOrderNumber = null
      this.existingError = ''
      this.existingModal = true
    },

async confirmAddToExisting() {
  this.existingError = ''
  if (!this.existingOrderNumber || !this.existingProductCtx) return

  const db = firebase.firestore()
  const p = this.existingProductCtx
  const vid = this.selectedVar[p.id] || null

  const foundVar = vid && p.variations ? p.variations.find(x => x.id === vid) : null
  const vName = foundVar && foundVar.name ? foundVar.name : ''

  const unitPrice = this.priceWithVar(p)
  const qty = Math.max(1, Number(this.existingQty || 1))
console.log('DEBUG →', { p, unitPrice, qty })

  // تأكد من عدم وجود قيم undefined
  if (!p.id || !p.Name || unitPrice === undefined || isNaN(unitPrice) || !qty) {
    this.existingError = 'بيانات المنتج غير كاملة أو غير صحيحة.'
    return
  }

  try {
    // ابحث عن الطلبية برقمها
    const q = await db.collection('orders').where('orderNumber', '==', Number(this.existingOrderNumber)).limit(1).get()
    if (q.empty) {
      this.existingError = 'لم يتم العثور على طلبية بهذا الرقم'
      return
    }

    const orderRef = q.docs[0].ref
await db.runTransaction(async (tx) => {
  const snap = await tx.get(orderRef)
  if (!snap.exists) throw new Error('Order not found')

  const data = snap.data() || {}
  const list = data.cart ? [...data.cart] : []

  const i = list.findIndex(it => it.productId === p.id && it.variationId === vid)
  if (i > -1) {
    list[i].quantity = Number(list[i].quantity || 0) + qty
  } else {
    list.push({
      productId: p.id,
      Name: p.Name || '',
      Price: unitPrice || 0,
      quantity: qty,
      imageUrl: p.imageUrl || '',
      variationId: vid || '',
      variationName: vName || ''
    })
  }

  tx.update(orderRef, { cart: list })
})


    this.$toast && this.$toast.success('تمت إضافة المنتج للطلبية')
    this.existingModal = false

  } catch (e) {
    console.error(e)
    this.existingError = 'تعذر الإضافة، حاول مجددًا'
  }
}

  }
}
</script>

<style scoped>
.catalogContainer{ padding: 16px; }
.toolbar{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.title h2{ margin:0; }
.actions{ display:flex; gap:8px; align-items:center; }
.actions input, .actions select{ height:36px; padding:0 10px; border:1px solid #ddd; border-radius:10px; }
.hint{ margin:8px 0 16px; }
.muted{ color:#757575; }
.small{ font-size:12px; }
.grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap:14px; }
.card{ background:#fff; border:1px solid #eee; border-radius:16px; overflow:hidden; display:flex; flex-direction:column; }
.thumb{ background-size:cover; background-position:center; width:100%; padding-top:70%; }
.info{ padding:10px 12px; display:grid; gap:6px; }
.name{ font-weight:700; }
.price{ font-weight:600; }
.vars select{ width:100%; height:34px; border:1px solid #e5e5e5; border-radius:8px; }
.cta{ display:flex; gap:8px; padding:10px 12px 12px; margin-top:auto; }
.cta button{ flex:1; height:38px; border:none; border-radius:10px; background:#111; color:#fff; cursor:pointer; }
.cta .secondary{ background:#f5f5f5; color:#111; }
.loadMore{ display:flex; justify-content:center; margin:16px 0; }
.loadingModal{ position:fixed; inset:0; background:rgba(255,255,255,0.6); display:flex; align-items:center; justify-content:center; z-index:30; }
.loader{ width:36px; height:36px; border:4px solid #ccc; border-top-color:#111; border-radius:50%; animation:spin 1s linear infinite; }
@keyframes spin{ to{ transform:rotate(360deg) } }

/* مودال */
.modal{ position:fixed; inset:0; background:rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; z-index:50; }
.modal-box{ width:min(520px, 95vw); background:#fff; border-radius:16px; overflow:hidden; }
.modal-head{ padding:12px 16px; display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #eee; }
.modal-body{ padding:16px; display:grid; gap:8px; }
.modal-body input{ height:38px; border:1px solid #ddd; border-radius:10px; padding:0 10px; }
.modal-foot{ padding:12px 16px; border-top:1px solid #eee; display:flex; justify-content:flex-end; gap:8px; }
.modal-foot button{ height:38px; border:none; border-radius:10px; background:#111; color:#fff; padding:0 16px; cursor:pointer; }
.close{ background:transparent; border:none; font-size:20px; cursor:pointer; }

/* موبايل تحسينات بسيطة */
@media (max-width: 640px){
  .actions{ width:100%; }
  .grid{ grid-template-columns: repeat(2, 1fr); }
}
</style>


// ====================================================================
// 4) ملاحظات تشغيلية سريعة
// ====================================================================
// - لإنشاء صفحة جديدة: أضف مستند داخل catalog_pages:
//   {
//     slug: 'big-cakes',
//     title: 'עוגות גדולות',
//     isActive: true,
//     filters: { categories: ['big-cakes'], inStockOnly: true },
//     sort: { by: 'createdAt', dir: 'desc' }
//   }
// ثم ادخل /catalog/big-cakes
//
// - ربط الطلبية الجديدة: صفحة إنشاء طلبية جديدة عندك تقرأ من localStorage المفتاح 'newOrderDraft'
//   وتدمجه مع selectedProducts (إذا بدك، بعد الإرسال امسح المسودة). 
//
// - البحث: يتطلب حقل name_lower داخل المنتج وتحديثه عند الإنشاء/التعديل.
//
// - الأمان: تحديث طلبية موجودة يفضّل يتم عبر Cloud Function مع صلاحيات،
//   الكود الحالي Transaction عميل لاختصار الطريق.



// WEBPACK FOOTER //
// src/pages/CatalogPage.vue